import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent,
  TextField,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { WASTE_TYPE_OPTIONS, WASTE_TYPE_OTHER } from '../../../constants/wasteTypes';
import { WEEKDAY_LABELS, WEEKDAYS_ORDER, Weekday } from '../../../types/organization-onboarding.type';
import { FileUploadField } from '../FileUploadField';
import { OnboardingStepProps } from './OnboardingStep1Basics';
import { getAllWasteCatalogMaterials, type WasteCatalogOption } from '../../../services/waste-catalog.service';

export function OnboardingStep3Operations({ form, errors, setField }: OnboardingStepProps) {
  const { t } = useTranslation();
  const [wasteTypeOptions, setWasteTypeOptions] = useState<WasteCatalogOption[]>(WASTE_TYPE_OPTIONS);

  useEffect(() => {
    // Materials, not categories: this is the same vocabulary as the waste-type
    // basket the organization gets once it exists, so the answer given here can
    // seed it directly instead of having to be translated.
    void getAllWasteCatalogMaterials()
      .then((options) => {
        if (options.length) setWasteTypeOptions(options);
      })
      .catch(() => undefined);
  }, []);

  const wasteChange = (e: SelectChangeEvent<string[]>) => {
    const v = e.target.value;
    const selected = typeof v === 'string' ? v.split(',') : v;
    setField('waste_types', selected);
    // Carried alongside so whoever reviews this application reads names, not ids.
    setField(
      'waste_type_names',
      selected.map((value) => wasteTypeOptions.find((o) => o.value === value)?.label ?? value),
    );
  };

  const daysChange = (e: SelectChangeEvent<Weekday[]>) => {
    const v = e.target.value;
    setField('operation_days', typeof v === 'string' ? (v.split(',') as Weekday[]) : v);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControl fullWidth required error={Boolean(errors.waste_types)}>
          <InputLabel id="waste-types-label">{t('onboarding.step3.wasteTypesHandled')}</InputLabel>
          <Select
            labelId="waste-types-label"
            multiple
            value={form.waste_types}
            onChange={wasteChange}
            input={<OutlinedInput label={t('onboarding.step3.wasteTypesHandled')} />}
            renderValue={(selected) =>
              selected
                .map((v) => wasteTypeOptions.find((o) => o.value === v)?.label ?? v)
                .join(', ')
            }
          >
            {wasteTypeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          {errors.waste_types ? <FormHelperText>{errors.waste_types}</FormHelperText> : null}
        </FormControl>
      </Grid>
      {form.waste_types.includes(WASTE_TYPE_OTHER) ? (
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label={t('onboarding.step3.otherWasteType')}
            value={form.other_waste_type}
            onChange={(e) => setField('other_waste_type', e.target.value)}
            error={Boolean(errors.other_waste_type)}
            helperText={errors.other_waste_type}
          />
        </Grid>
      ) : null}
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label={t('onboarding.step3.googleMapsLink')}
          value={form.google_maps_url}
          onChange={(e) => setField('google_maps_url', e.target.value)}
          error={Boolean(errors.google_maps_url)}
          helperText={errors.google_maps_url}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          multiline
          minRows={3}
          label={t('onboarding.step3.organizationAddress')}
          value={form.organization_address}
          onChange={(e) => setField('organization_address', e.target.value)}
          error={Boolean(errors.organization_address)}
          helperText={errors.organization_address}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          required
          label={t('onboarding.step3.nationalAddressShortCode')}
          value={form.national_address_short}
          onChange={(e) => setField('national_address_short', e.target.value)}
          error={Boolean(errors.national_address_short)}
          helperText={errors.national_address_short}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <FileUploadField
          label={t('onboarding.step3.nationalAddressDocument')}
          required
          value={form.national_address_document}
          onChange={(f) => setField('national_address_document', f)}
          error={errors.national_address_document}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl error={Boolean(errors.is_24_7)}>
          <FormLabel id="hours-247-label">{t('onboarding.step3.operate247')}</FormLabel>
          <RadioGroup
            row
            aria-labelledby="hours-247-label"
            name="is_24_7"
            value={form.is_24_7 === null ? '' : form.is_24_7 ? 'yes' : 'no'}
            onChange={(e) => setField('is_24_7', e.target.value === 'yes')}
          >
            <FormControlLabel value="yes" control={<Radio />} label={t('common.yes')} />
            <FormControlLabel value="no" control={<Radio />} label={t('common.no')} />
          </RadioGroup>
          {errors.is_24_7 ? <FormHelperText>{errors.is_24_7}</FormHelperText> : null}
        </FormControl>
      </Grid>
      {form.is_24_7 === false ? (
        <>
          <Grid item xs={12}>
            <FormControl fullWidth required error={Boolean(errors.operation_days)}>
              <InputLabel id="op-days-label">{t('onboarding.step3.operatingDays')}</InputLabel>
              <Select
                labelId="op-days-label"
                multiple
                value={form.operation_days}
                onChange={daysChange}
                input={<OutlinedInput label={t('onboarding.step3.operatingDays')} />}
                renderValue={(selected) =>
                  selected.map((d) => WEEKDAY_LABELS[d as Weekday]).join(', ')
                }
              >
                {WEEKDAYS_ORDER.map((d) => (
                  <MenuItem key={d} value={d}>
                    {WEEKDAY_LABELS[d]}
                  </MenuItem>
                ))}
              </Select>
              {errors.operation_days ? (
                <FormHelperText>{errors.operation_days}</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePicker
              label={t('onboarding.step3.openingTime')}
              value={form.operation_start}
              onChange={(d) => setField('operation_start', d)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(errors.operation_start),
                  helperText: errors.operation_start,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePicker
              label={t('onboarding.step3.closingTime')}
              value={form.operation_end}
              onChange={(d) => setField('operation_end', d)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: Boolean(errors.operation_end),
                  helperText: errors.operation_end,
                },
              }}
            />
          </Grid>
        </>
      ) : null}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('onboarding.step3.notes')}
          multiline
          minRows={2}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
        />
      </Grid>
    </Grid>
  );
}
